import { useEffect, useState } from 'react';
import { Canvas } from './ui/Canvas.js';
import { Toolbar } from './ui/Toolbar.js';
import { StylePanel } from './ui/StylePanel.js';
import { TopBar } from './ui/TopBar.js';
import { ActionsBar } from './ui/ActionsBar.js';
import { TextEditor } from './ui/TextEditor.js';
import { ShareDialog } from './ui/ShareDialog.js';
import { AuthDialog } from './ui/AuthDialog.js';
import { TemplateGallery } from './ui/TemplateGallery.js';
import { HelpPanel } from './ui/HelpPanel.js';
import { useKeyboardShortcuts } from './ui/keyboard.js';
import { useCanvas } from './state/canvas-store.js';
import { loadLocal } from './lib/persistence.js';
import { navigate, parseRoute, useRoute } from './lib/router.js';
import { api } from './lib/api.js';
import { newId } from '@extapia/shared';

const registerSW = () => {
  if ('serviceWorker' in navigator && import.meta.env.PROD) {
    void navigator.serviceWorker.register('/sw.js').catch(() => {
      /* ignore */
    });
  }
};

const ensureDocId = (): string => {
  const r = parseRoute();
  if (r.name === 'doc') return r.docId;
  const id = newId();
  navigate({ name: 'doc', docId: id });
  return id;
};

export const App = () => {
  useKeyboardShortcuts();

  const app = useCanvas((s) => s.app);
  const loadFromJSON = useCanvas((s) => s.loadFromJSON);
  const [route, docId] = useRouteAndDoc();
  const [shareOpen, setShareOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [resolving, setResolving] = useState(false);

  // Load previous session on mount
  useEffect(() => {
    const snap = loadLocal();
    if (snap && snap.elements.length > 0) loadFromJSON(snap);
    registerSW();
  }, [loadFromJSON]);

  // Apply theme
  useEffect(() => {
    document.documentElement.classList.toggle('dark', app.theme === 'dark');
  }, [app.theme]);

  // Handle share token in URL — resolve then redirect to doc
  useEffect(() => {
    if (route.name !== 'share') return;
    setResolving(true);
    api
      .resolveShare(route.token)
      .then((info) => navigate({ name: 'doc', docId: info.docId }))
      .catch(() => alert('Share link tidak valid atau sudah kedaluwarsa'))
      .finally(() => setResolving(false));
  }, [route.name === 'share' ? route.token : null]);

  // Help panel via keyboard event
  useEffect(() => {
    const openHelp = () => setHelpOpen(true);
    window.addEventListener('open-help', openHelp);
    return () => window.removeEventListener('open-help', openHelp);
  }, []);

  if (resolving) {
    return (
      <div className="w-full h-full grid place-items-center">
        <div className="text-sm text-neutral-500">Resolving share link…</div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full overflow-hidden">
      <Canvas />
      <TextEditor />
      <StylePanel />
      <Toolbar />
      <TopBar
        onOpenShare={() => setShareOpen(true)}
        onOpenTemplates={() => setTemplatesOpen(true)}
        onOpenAuth={() => setAuthOpen(true)}
        onOpenHelp={() => setHelpOpen(true)}
      />
      <ActionsBar />

      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} docId={docId} />
      <AuthDialog open={authOpen} onClose={() => setAuthOpen(false)} />
      <TemplateGallery open={templatesOpen} onClose={() => setTemplatesOpen(false)} />
      <HelpPanel open={helpOpen} onClose={() => setHelpOpen(false)} />

      <footer className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 text-[11px] text-neutral-500 dark:text-neutral-400 bg-white/70 dark:bg-neutral-900/70 backdrop-blur px-2.5 py-1 rounded-full ring-1 ring-black/5">
        Excalidraw ExTapia · v0.2 · doc <code className="font-mono">{docId.slice(0, 6)}</code> · autosaved locally
      </footer>
    </div>
  );
};

// Small hook that keeps a stable docId per route
const useRouteAndDoc = (): [ReturnType<typeof useRoute>, string] => {
  const route = useRoute();
  const [docId, setDocId] = useState(() => ensureDocId());
  useEffect(() => {
    if (route.name === 'doc') setDocId(route.docId);
    else if (route.name === 'home') setDocId(ensureDocId());
  }, [route.name, route.name === 'doc' ? route.docId : null]);
  return [route, docId];
};
